import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { SilaeCodeEditor } from "@/components/silae/silae-code-editor";

export const CodeEditorTab: React.FC = () => {
  const { t } = useTranslation();

  const [code, setCode] = useState(`Begin
  // ${t("codeEditor.defaultCode.comment")}
  If bul.mois = 3 Then
    Exec("Lprime_D02")
    methodeCalcul = 2
    Bases = 300
    Liblong = "${t("codeEditor.defaultCode.libelle")}"
    Exec("GenereLprime")
  Endif
End`);

  const [fileName, setFileName] = useState(t("codeEditor.defaultFileName"));

  // Fonction pour exécuter le code (simulation)
  const handleRunCode = () => {
    toast.success(t("codeEditor.messages.codeExecuted"));
  };

  // Fonction pour sauvegarder le code
  const handleSaveCode = () => {
    const blob = new Blob([code], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(t("codeEditor.messages.codeSaved"));
  };

  // Fonction pour charger un fichier
  const handleLoadFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const content = e.target?.result as string;
        setCode(content);
        setFileName(file.name);
        toast.success(t("codeEditor.messages.fileLoaded"));
      };
      reader.readAsText(file);
    }
  };

  // Fonction pour copier le code
  const handleCopyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success(t("codeEditor.messages.codeCopied"));
  };

  // Fonction pour effacer le code
  const handleClearCode = () => {
    setCode("Begin\n  \nEnd");
    toast.success(t("codeEditor.messages.codeCleared"));
  };

  // Fonction pour charger un exemple
  const handleLoadExample = () => {
    const exampleCode = `Begin
  // ${t("codeEditor.exampleCode.comment")}
  If bul.mois = 12 Then
    Exec("Lprime_D05")
    methodeCalcul = 2
    
    // ${t("codeEditor.exampleCode.baseCalculation")}
    datedeb = Date(1,1,bul.annee)
    datefin = Date(31,12,bul.annee)
    SalBase = cumulbulletins(datedeb,datefin,0)
    Bases = SalBase / 12
    
    // ${t("codeEditor.exampleCode.forceBase")}
    BaseForce = Saisie("13Mois",0)
    If BaseForce > 0 Then Bases = BaseForce
    If BaseForce < 0 Then Bases = 0
    
    Liblong = "${t("codeEditor.exampleCode.libelle")}"
    
    // ${t("codeEditor.exampleCode.generation")}
    If Bases <> 0 Then Exec("GenereLprime")
    
    // ${t("codeEditor.exampleCode.storage")}
    call stockevar("Pr_13Mois",Bases)
  Endif
End`;
    setCode(exampleCode);
    toast.success(t("codeEditor.messages.exampleLoaded"));
  };

  return (
    <div className="space-y-6 p-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("codeEditor.title")}</h1>
          <p className="text-muted-foreground">{t("codeEditor.description")}</p>
        </div>
      </div>

      <SilaeCodeEditor
        value={code}
        onChange={setCode}
        placeholder={t("codeEditor.placeholder")}
        fileName={fileName}
        onFileNameChange={setFileName}
        onRunCode={handleRunCode}
        onSaveCode={handleSaveCode}
        onLoadFile={handleLoadFile}
        onCopyCode={handleCopyCode}
        onClearCode={handleClearCode}
        onLoadExample={handleLoadExample}
        className="min-h-[900px]"
      />

      {/* Informations */}
      <Card>
        <CardHeader>
          <CardTitle>{t("codeEditor.shortcuts.title")}</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="text-sm text-muted-foreground space-y-1 flex flex-wrap  gap-4">
            <li>
              •{" "}
              <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+Z</kbd>{" "}
              : {t("codeEditor.shortcuts.undo")}
            </li>
            <li>
              •{" "}
              <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Ctrl+Y</kbd>{" "}
              : {t("codeEditor.shortcuts.redo")}
            </li>
            <li>
              •{" "}
              <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Entrée</kbd>{" "}
              : {t("codeEditor.shortcuts.autoIndent")}
            </li>
            <li>
              • <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Tab</kbd>{" "}
              : {t("codeEditor.shortcuts.indent")}
            </li>
            <li>
              • <kbd className="px-1 py-0.5 bg-muted rounded text-xs">↑↓</kbd> :
              {t("codeEditor.shortcuts.navigateSuggestions")}
            </li>
            <li>
              •{" "}
              <kbd className="px-1 py-0.5 bg-muted rounded text-xs">Échap</kbd>{" "}
              : {t("codeEditor.shortcuts.closeSuggestions")}
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};
